import { Injectable, Injector } from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
  chain,
  find,
  get,
  has,
  head,
  isEmpty,
  isEqual,
  keyBy,
  map,
  merge,
  toLower,
} from 'lodash';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

import { SiteType } from '@app/shared/types/enums/site-type.enum';
import { ENTITY_TYPES } from '@app/shared/types/enums/entityTypes.enum';
import { IEntityConfig } from '@app/shared/types/entities/entity-config.type';
import { IBaseEntityType } from '@app/shared/types/entities/base/base.type';
import {
  IEvent,
  IEventStatus,
} from '@app/shared/types/entities/xprt-entities/events/events.type';
import { setFormFieldValue } from '@components/form/services/schema-form/schema-form-functions';
import { lazyInject } from '../../../containers/base/decorators/lazy-inject.decorator';

import {
  BTN_CANCEL,
  BTN_DONE,
  BTN_SELECT_EVENT_AND_SITE,
} from '../../../text-content/button-templates/button-templates-content';
import { EVENT_SELECTOR_SELECT, WIDGET_FAMILY } from '@config/global-constants';
import { XprtDataActions } from '@app/store/actions/xprt-data/xprt-data.actions';
import { WIDGET_COMPONENT } from '@app/shared/types/enums/widget-component.enum';
import {
  IModalDialogOptions,
  IModalDialogResult,
} from '@app/shared/types/ui/modal-options.type';
import { HawkerSessionService } from '../hawker-session/hawker-session.service';
import { I18n } from '@app/i18n/strings-keys';
import { rx } from '@app/shared/rx/rx-wrapper';
import { canAddPermission } from '../hawker-session/hawker-session.utils';
const adminConfig: IEntityConfig = merge({}, HawkerSessionService.config, {
  ENTITY_TYPE: ENTITY_TYPES.HAWKER_SESSION_ADMIN,
  apiEndpoint: ENTITY_TYPES.HAWKER_SESSION,
  uiRoute: ENTITY_TYPES.HAWKER_SESSION_ADMIN,
  REDIRECT_ENTITY_TYPE: ENTITY_TYPES.HAWKER_SESSION_ADMIN,
  entityNameToCheckPermissions: ENTITY_TYPES.HAWKER_CONFIG,
  suppressPermissionEntityTypeUsageForNavigation: true,
  buttons: {},
  list: {
    titleObs: new BehaviorSubject(null),
    canAdd: canAddPermission,
  },
  editor: {
    canAdd: canAddPermission,
  },
});

@Injectable()
export class HawkerSessionAdminService extends HawkerSessionService {
  public static config: IEntityConfig = adminConfig;
  public static override ENTITY_TYPE: string = adminConfig.ENTITY_TYPE;

  @lazyInject(XprtDataActions) public xprtDataActions: XprtDataActions;

  private readonly adminSelectionState$: BehaviorSubject<{
    siteId: string;
    eventId: string;
    eventStatus: IEventStatus | null;
  }> = new BehaviorSubject({ siteId: '', eventId: '', eventStatus: null });

  private selectedEventStatus: IEventStatus | null = null;

  public override readonly createButtonDisabled$: Observable<boolean> =
    this.adminSelectionState$.pipe(
      rx.map(
        ({ siteId, eventId, eventStatus }) =>
          isEmpty(siteId) ||
          isEmpty(eventId) ||
          eventStatus === IEventStatus.Closed,
      ),
    );

  public override beforeStartListMethods: Function[] = [
    this.hookXuxListContainer,
    this.initAndPromptAdminSelection,
  ];

  public override beforeDestroyListMethods: Function[] = [
    this.cleanupAdminSubscriptions,
    this.cleanupSubscriptions,
  ];

  public constructor(public override injector: Injector) {
    super(injector);

    this.setupAdminButtons();
  }

  public initAndPromptAdminSelection(): void {
    if (isEmpty(this.selectedSiteId) && isEmpty(this.selectedEventId)) {
      this.promptEventThenSiteSelection();
    } else {
      this.adminSelectionState$.next({
        siteId: this.selectedSiteId,
        eventId: this.selectedEventId,
        eventStatus: this.selectedEventStatus,
      });
    }
  }

  private async fetchSelectableAdminEvents(): Promise<IEvent[]> {
    return ((await this.xprtDataService
      .getEventsByFilter({
        filter: `status eq in(${IEventStatus.Open},${IEventStatus.Closed})`,
        select: ['id', 'name', 'status', 'event_days', 'available_sites'],
        orderby: 'status desc,event_days/start_date_time desc',
        include_available_sites: true,
        available_sites_select: 'id,name,store_number,business_type,metadata',
        filter_by_assigned_sites: true,
      })
      .toPromise()) ?? []) as IEvent[];
  }

  private buildAdminSelectionModalOptions(options: {
    eventsForModal$: BehaviorSubject<{ text: string; value: string }[]>;
    sitesSelectOptions$: BehaviorSubject<{ text: string; value: string }[]>;
    sitesForModal$: BehaviorSubject<IBaseEntityType[]>;
    siteFieldDisabled$: BehaviorSubject<boolean>;
    onFormReady: (form: FormGroup) => void;
  }): IModalDialogOptions {
    const {
      eventsForModal$,
      sitesSelectOptions$,
      sitesForModal$,
      siteFieldDisabled$,
      onFormReady,
    } = options;

    return {
      title: I18n.Common.SelectEventAndSite,
      validation: true,
      buttons: [
        {
          ...BTN_DONE,
          caption: I18n.Common.Submit,
          disabled: (value: any): boolean =>
            isEmpty(value.eventId) || isEmpty(value.siteId),
        },
        BTN_CANCEL,
      ],
      value: {
        eventId: isEmpty(this.selectedEventId) ? [] : [this.selectedEventId],
        siteId: isEmpty(this.selectedSiteId) ? [] : [this.selectedSiteId],
      },
      schema: {
        type: 'object',
        properties: {
          eventId: { type: 'array' },
          siteId: { type: 'array' },
        },
      },
      onFormReady,
      formRules: {
        fields: {
          eventId: {
            required: true,
            title: I18n.EntityNames.Event,
            widgetFamily: WIDGET_FAMILY.XUX,
            widget: WIDGET_COMPONENT.select_event_with_grid_modal,
            selectOptions: eventsForModal$,
            widgetData: {
              availableEventStatus: [IEventStatus.Open, IEventStatus.Closed],
              sortForPortalSelector: [
                'status desc',
                'event_days/start_date_time desc',
              ],
              isMultiple: false,
              minItems: 1,
              maxItems: 1,
              select: EVENT_SELECTOR_SELECT,
            },
          },
          siteId: {
            required: true,
            title: I18n.EntityNames.Site,
            widgetFamily: WIDGET_FAMILY.XUX,
            widget: WIDGET_COMPONENT.select_site_with_grid_modal,
            selectOptions: sitesSelectOptions$,
            disabledObs: siteFieldDisabled$,
            widgetData: {
              dataSource: sitesForModal$,
              isMultiple: false,
            },
          },
        },
      },
    };
  }

  private applyAdminSelectionResult(
    result: IModalDialogResult,
    sitesForModal$: BehaviorSubject<IBaseEntityType[]>,
    currentFullEvent: IEvent | null,
  ): void {
    if (!result.confirmed) {
      return;
    }

    const selectedEventId = head(result.value.eventId as string[]);
    const selectedSiteId = head(result.value.siteId as string[]);

    if (!selectedEventId || !selectedSiteId || !currentFullEvent) {
      return;
    }

    this.commissarySites = sitesForModal$.getValue();
    this.selectedSiteId = selectedSiteId;
    this.selectedEventId = selectedEventId;
    this.selectedEventStatus = currentFullEvent.status as IEventStatus;
    this.adminSelectionState$.next({
      siteId: selectedSiteId,
      eventId: selectedEventId,
      eventStatus: currentFullEvent.status as IEventStatus,
    });
    this.applyEventSelection(currentFullEvent);
  }

  public async promptEventThenSiteSelection(): Promise<void> {
    this.uiActions.enableLoader();
    const fetchedEvents = await this.fetchSelectableAdminEvents().finally(() =>
      this.uiActions.disableLoader(),
    );

    const eventsForModal$ = new BehaviorSubject(
      map(fetchedEvents, (event: IEvent) => ({
        text: get(event, 'name', ''),
        value: get(event, 'id', ''),
      })),
    );
    const sitesForModal$ = new BehaviorSubject<IBaseEntityType[]>([]);
    const sitesSelectOptions$ = new BehaviorSubject<
      { text: string; value: string }[]
    >([]);
    const siteFieldDisabled$ = new BehaviorSubject<boolean>(true);
    let currentFullEvent: IEvent | null = null;
    let onFormReadySubscription: Subscription | null = null;

    const onFormReady = (form: FormGroup): void => {
      const initialEventIds = [
        ...((form.get('eventId').value as string[]) ?? []),
      ];
      const initialSiteIds = [
        ...((form.get('siteId').value as string[]) ?? []),
      ];
      let shouldRestoreInitialSiteSelection =
        !isEmpty(initialEventIds) && !isEmpty(initialSiteIds);

      const loadSitesForEvent = async (
        eventId: string,
        clearSite: boolean = true,
      ): Promise<boolean> => {
        if (clearSite) {
          form.get('siteId').setValue([], { emitEvent: false });
        }
        sitesForModal$.next([]);
        sitesSelectOptions$.next([]);
        siteFieldDisabled$.next(true);
        currentFullEvent = null;

        if (!eventId) {
          return false;
        }

        const fullEvent = find(fetchedEvents, { id: eventId }) ?? null;
        if (!fullEvent) {
          return false;
        }

        try {
          currentFullEvent = fullEvent;
          const sites = await this.fetchAdminSitesForEvent(fullEvent);
          sitesForModal$.next(sites);
          sitesSelectOptions$.next(
            map(sites, (site) => ({
              text: get(site, 'name', ''),
              value: get(site, 'id', ''),
            })),
          );

          return true;
        } catch {
          currentFullEvent = null;

          return false;
        } finally {
          siteFieldDisabled$.next(false);
        }
      };

      onFormReadySubscription = form
        .get('eventId')
        .valueChanges.pipe(distinctUntilChanged(isEqual))
        .subscribe(async (eventIds: string[]) => {
          const shouldRestoreSite =
            shouldRestoreInitialSiteSelection &&
            isEqual(eventIds, initialEventIds);

          shouldRestoreInitialSiteSelection = false;
          const sitesLoaded = await loadSitesForEvent(
            head(eventIds),
            !shouldRestoreSite,
          );

          if (shouldRestoreSite && sitesLoaded) {
            setFormFieldValue(form, 'siteId', initialSiteIds);
          }
        });

      if (!isEmpty(initialEventIds)) {
        setFormFieldValue(form, 'eventId', initialEventIds);
      }
    };

    const result = await this.uiActions.promptFormModal(
      this.buildAdminSelectionModalOptions({
        eventsForModal$,
        sitesSelectOptions$,
        sitesForModal$,
        siteFieldDisabled$,
        onFormReady,
      }),
    );
    onFormReadySubscription?.unsubscribe();

    this.applyAdminSelectionResult(result, sitesForModal$, currentFullEvent);
  }

  public async fetchAdminSitesForEvent(
    event: IEvent,
  ): Promise<IBaseEntityType[]> {
    const eventSiteIds = keyBy(
      get(event, 'available_sites', []) as IBaseEntityType[],
      (site) => get(site, 'id', ''),
    );

    const userAssignedSites = await this.xprtDataService
      .getAllSitesByFilter(
        {
          filter_by_assigned_sites: true,
          select: ['id', 'name', 'store_number', 'business_type', 'metadata'],
        },
        { throwError: true },
      )
      .toPromise();

    return chain(userAssignedSites as IBaseEntityType[])
      .filter(
        (site: IBaseEntityType) =>
          has(eventSiteIds, get(site, 'id', '')) &&
          get(site, 'business_type') === SiteType.VendingCommissary,
      )
      .map((site) => ({ ...site, event }))
      .sortBy((site) => toLower(get(site, 'store_number', '')))
      .value();
  }

  public selectEventAndSiteHandler = async (): Promise<void> => {
    await this.promptEventThenSiteSelection();
  };

  public cleanupAdminSubscriptions(): void {
    this.adminSelectionState$.next({
      siteId: '',
      eventId: '',
      eventStatus: null,
    });
  }

  private setupAdminButtons(): void {
    const baseButtons = this.config.buttons;

    this.config.buttons = {
      ...baseButtons,
      select_site_and_event: {
        ...BTN_SELECT_EVENT_AND_SITE,
        class: 'btn btn-default',
        click: this.selectEventAndSiteHandler,
      },
      create: {
        click: this.navigateToAddWithParams,
        disabledObs: this.createButtonDisabled$,
      },
      select_site: false,
    };
  }
}
